import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faSearch, faSpinner, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Input, Button } from '../../../../ui';
import { toast } from 'react-toastify';
import {
    GoogleCandidate,
    NearbyCoffeeShop,
    searchGoogleShops,
    proposeGoogleCandidate,
    proposeManualShop
} from '../shopSearch';

interface AddCoffeeShopDialogProps {
    open: boolean;
    onClose: () => void;
    onShopAdded: (shop: NearbyCoffeeShop) => void;
    lat: number;
    lng: number;
    city?: string;
    country?: string;
    clusterId?: string;
    initialShopName?: string;
}

const AddCoffeeShopDialog: React.FC<AddCoffeeShopDialogProps> = ({
    open,
    onClose,
    onShopAdded,
    lat,
    lng,
    city = 'Unknown',
    country = 'Unknown',
    clusterId,
    initialShopName
}) => {
    const [shopName, setShopName] = useState('');
    const [manualAddress, setManualAddress] = useState('');
    const [manualLat, setManualLat] = useState('');
    const [manualLng, setManualLng] = useState('');
    const [candidates, setCandidates] = useState<GoogleCandidate[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isProposing, setIsProposing] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [manualExpanded, setManualExpanded] = useState(false);

    // sync manual coords when dialog opens with new lat/lng
    React.useEffect(() => {
        if (open && (lat !== 0 || lng !== 0)) {
            setManualLat(String(lat));
            setManualLng(String(lng));
        }
    }, [open, lat, lng]);

    // prefill the typed name when the dialog opens from the inline search
    React.useEffect(() => {
        if (open && initialShopName) {
            setShopName(initialShopName);
        }
    }, [open, initialShopName]);

    const handleSearch = async () => {
        if (!shopName.trim()) return;
        setIsSearching(true);
        setCandidates([]);
        try {
            const results = await searchGoogleShops(shopName, lat, lng);
            setHasSearched(true);
            setCandidates(results);
        } catch (err) {
            console.error('Google search failed:', err);
            toast.error('Search failed');
            setCandidates([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectCandidate = async (c: GoogleCandidate) => {
        setIsProposing(true);
        try {
            const shop = await proposeGoogleCandidate(c, { city, country, clusterId });
            if (shop) {
                onShopAdded(shop);
                onClose();
            }
        } catch (err) {
            console.error('Propose failed:', err);
            toast.error('Failed to add shop');
        } finally {
            setIsProposing(false);
        }
    };

    const handleAddManually = async () => {
        if (!shopName.trim()) return;
        const latitude = manualLat.trim() ? parseFloat(manualLat) : lat;
        const longitude = manualLng.trim() ? parseFloat(manualLng) : lng;
        if (isNaN(latitude) || isNaN(longitude)) {
            toast.error('Please enter valid coordinates or use your current location');
            return;
        }
        setIsProposing(true);
        try {
            const address = manualAddress.trim() || `${city}, ${country}`;
            const shop = await proposeManualShop({
                name: shopName.trim(),
                lat: latitude,
                lng: longitude,
                address,
                city,
                country,
                clusterId
            });
            if (shop) {
                onShopAdded(shop);
                toast.success('Shop added. It will be reviewed before appearing in listings.');
                onClose();
            }
        } catch (err) {
            console.error('Propose failed:', err);
            toast.error('Failed to add shop');
        } finally {
            setIsProposing(false);
        }
    };

    const handleClose = () => {
        setShopName('');
        setManualAddress('');
        setManualLat('');
        setManualLng('');
        setCandidates([]);
        setHasSearched(false);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faStore} className="h-4 w-4" />
                        Add new coffee shop
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium mb-2 block">Shop name</label>
                        <div className="flex gap-2">
                            <Input
                                value={shopName}
                                onChange={(e) => setShopName(e.target.value)}
                                placeholder="e.g. Coffee Lab"
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <Button onClick={handleSearch} disabled={isSearching || !shopName.trim()}>
                                {isSearching ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faSearch} />}
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Searches within 2 km of your location. Search by exact shop name for best results.</p>
                    </div>

                    {hasSearched && !isSearching && (
                        <p className="text-sm text-muted-foreground">
                            {candidates.length > 0
                                ? `${candidates.length} result${candidates.length === 1 ? '' : 's'} from Google`
                                : 'No results found'}
                        </p>
                    )}

                    {candidates.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">Select from Google results</p>
                            <div className="max-h-48 overflow-y-auto space-y-2 border rounded-lg p-2">
                                {candidates.map((c) => (
                                    <button
                                        key={c.place_id}
                                        type="button"
                                        onClick={() => handleSelectCandidate(c)}
                                        disabled={isProposing}
                                        className="w-full text-left p-2 rounded bg-muted/30 hover:bg-muted/50 transition-colors disabled:opacity-50"
                                    >
                                        <div className="font-medium text-sm">{c.name}</div>
                                        <div className="text-xs text-muted-foreground truncate">
                                            {c.formatted_address || c.vicinity}
                                        </div>
                                        {c.rating && (
                                            <span className="text-xs text-gold">★ {c.rating}</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="pt-2 border-t">
                        <button
                            type="button"
                            onClick={() => setManualExpanded((v) => !v)}
                            className="w-full flex items-center gap-2 text-sm font-medium text-left py-1.5 hover:bg-muted/30 rounded transition-colors"
                        >
                            <FontAwesomeIcon icon={manualExpanded ? faChevronDown : faChevronRight} className="text-muted-foreground w-3" />
                            Or add manually
                        </button>
                        {manualExpanded && (
                            <div className="space-y-3 mt-3">
                                <div
                                    className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
                                    role="alert"
                                >
                                    Manual entries require admin review. The shop won&apos;t appear in public listings until approved.
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Shop name</label>
                                    <Input
                                        value={shopName}
                                        onChange={(e) => setShopName(e.target.value)}
                                        placeholder="e.g. Coffee Lab"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Address (optional)</label>
                                    <Input
                                        value={manualAddress}
                                        onChange={(e) => setManualAddress(e.target.value)}
                                        placeholder={`e.g. 123 Main St, ${city}`}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Latitude (optional)</label>
                                        <Input
                                            type="text"
                                            inputMode="decimal"
                                            value={manualLat}
                                            onChange={(e) => setManualLat(e.target.value)}
                                            placeholder={String(lat)}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium mb-1 block">Longitude (optional)</label>
                                        <Input
                                            type="text"
                                            inputMode="decimal"
                                            value={manualLng}
                                            onChange={(e) => setManualLng(e.target.value)}
                                            placeholder={String(lng)}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground">Leave coordinates empty to use your current location.</p>
                                <Button
                                    variant="outline"
                                    onClick={handleAddManually}
                                    disabled={isProposing || !shopName.trim()}
                                >
                                    Add manually (pending review)
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddCoffeeShopDialog;
